import {
  Autorenew as AutoreNewIcon,
  Close as CloseIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import {
  Alert,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import {
  CalendarIcon,
  DatePicker,
  LocalizationProvider,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { FC, useState } from 'react';
import NoAssemblySearchImage from 'src/assets/images/NoAssemblySearch.svg?react';
import { CardGrid, CardGridItem } from 'src/components/cardGrid';
import { SearchBar } from 'src/components/input';
import { InputTextLabel } from 'src/components/input/Input.styled';
import { NoResultAlert } from 'src/components/noResultAlert';
import { Progress } from 'src/components/progress/Progress';
import { AssemblyPreviewSidebar } from 'src/features/assemblies/listAssemblyPage/modules/assemblyPreviewSidebar/AssemblyPreviewSidebar';
import { EmptyAssembly } from 'src/features/assemblies/listAssemblyPage/modules/emptyAssembly/EmptyAssembly';
import { AssemblyListCard } from 'src/features/locations/timingLocationGroupPage/modules/assemblyListModalCard';
import { IAssembly } from 'src/interfaces';
import { lang } from 'src/utils';

import {
  HeaderTitle,
  SearchAndCloseIconWrapper,
} from '../componentListModal/ComponentListModal.styled';
import { Modal } from '../Modal';
import { useFilteredAssemblies } from './AssemblyListModal.hook';
import { AssemblyListModalProps } from './AssemblyListModal.interface';
import {
  AlertContainer,
  Container,
  CreateAssemblyButton,
  FluidWidthColumn,
  HeaderWrapper,
} from './AssemblyListModal.styled';

export const AssemblyListModal = (props: AssemblyListModalProps) => {
  const {
    assembliesData,
    isLoadingAssemblies,
    errorLoadingAssemblies,
    setSelectedAssembly,
    selectedAssembly,
    inputValue,
    searchAssemblies,
    getNextDefaultAssemblyPage,
  } = useFilteredAssemblies(
    props?.channelName,
    props?.layoutTypeId,
    props?.excludeAssemblyIds,
  );

  const [startDate, setStartDate] = useState('');
  const [startDateError, setStartDateError] = useState<string | null>(null);
  const isStartDateValid =
    !!startDate &&
    dayjs(startDate).isValid() &&
    dayjs(startDate).isAfter(dayjs(), 'day') &&
    !startDateError;

  const showEmptyAssembly =
    assembliesData &&
    assembliesData.length === 0 &&
    inputValue === '' &&
    !isLoadingAssemblies;

  const handleCancel = () => {
    props.onClose();
  };

  return (
    <Modal {...props} onClose={handleCancel}>
      <Container>
        {showEmptyAssembly ? (
          <EmptyAssembly
            message={lang.noMatchingAssemblies}
            image={NoAssemblySearchImage as unknown as FC}
          />
        ) : (
          <>
            <DialogContent>
              <HeaderWrapper>
                <HeaderTitle>
                  {props.title ?? lang.scheduleNewDefaultAssembly}
                </HeaderTitle>

                <SearchAndCloseIconWrapper>
                  <SearchBar
                    onInputChange={searchAssemblies}
                    placeholder={'Search'}
                  />
                  <IconButton aria-label="close" onClick={handleCancel}>
                    <CloseIcon />
                  </IconButton>
                </SearchAndCloseIconWrapper>
              </HeaderWrapper>
              {props.scheduleAssemblySelected && (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Grid container spacing={2} mb={2} mt={1}>
                    <Grid item md={4}>
                      <InputTextLabel>{lang.startDate}</InputTextLabel>
                      <DatePicker
                        sx={{ width: '100%' }}
                        minDate={dayjs().add(1, 'day').startOf('day')}
                        disablePast
                        onChange={(value) => {
                          const selectedDate = value as unknown as Dayjs;
                          const nextStartDate = selectedDate?.isValid()
                            ? selectedDate.toISOString()
                            : '';

                          setStartDate(nextStartDate);
                          setStartDateError(
                            nextStartDate &&
                              dayjs(nextStartDate).isAfter(dayjs(), 'day')
                              ? null
                              : 'minDate',
                          );
                        }}
                        onError={(error) => {
                          setStartDateError(error);
                        }}
                      />
                    </Grid>
                  </Grid>
                </LocalizationProvider>
              )}
              {isLoadingAssemblies ? (
                <Progress />
              ) : errorLoadingAssemblies ? (
                <Typography>{lang.error}</Typography>
              ) : (
                <>
                  {assembliesData &&
                  assembliesData.length === 0 &&
                  inputValue !== '' &&
                  !isLoadingAssemblies ? (
                    <NoResultAlert />
                  ) : (
                    <>
                      {props.message && (
                        <AlertContainer>
                          <Alert severity="info">{props.message}</Alert>
                        </AlertContainer>
                      )}
                      <Grid container spacing={4} mt={1}>
                        <FluidWidthColumn
                          item
                          xs={12}
                          md={selectedAssembly === null ? 12 : 8}>
                          <CardGrid
                            isLoading={isLoadingAssemblies}
                            error={errorLoadingAssemblies}
                            updatePage={getNextDefaultAssemblyPage}>
                            {assembliesData.map((assembly) => (
                              <CardGridItem item key={assembly.id}>
                                <AssemblyListCard
                                  card={assembly}
                                  selectedAssemblyId={selectedAssembly?.id}
                                  onSelectedAssembly={setSelectedAssembly}
                                />
                              </CardGridItem>
                            ))}
                          </CardGrid>
                        </FluidWidthColumn>
                        {selectedAssembly && (
                          <Grid item xs={12} md={4}>
                            <AssemblyPreviewSidebar
                              highlightedAssembly={selectedAssembly}
                              hasSidebarPreviewLoaded={true}
                              hideSidebarPreviewButton={true}
                              isLoadingSidebarPreview={false}
                            />
                          </Grid>
                        )}
                      </Grid>
                    </>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions>
              <CreateAssemblyButton
                variant="text"
                disabled={
                  !selectedAssembly ||
                  (props.scheduleAssemblySelected && !isStartDateValid)
                }
                onClick={() =>
                  props.onSelectedAssembly(
                    selectedAssembly as IAssembly,
                    startDate,
                  )
                }
                startIcon={
                  props.scheduleAssemblySelected ? (
                    <CalendarIcon />
                  ) : props.excludeAssemblyIds &&
                    props.excludeAssemblyIds.length > 0 ? (
                    <AutoreNewIcon />
                  ) : (
                    <FileDownloadIcon />
                  )
                }>
                {props.scheduleAssemblySelected
                  ? lang.schedule
                  : props.excludeAssemblyIds &&
                    props.excludeAssemblyIds.length > 0
                  ? lang.replace
                  : lang.addAssembly}
              </CreateAssemblyButton>
            </DialogActions>
          </>
        )}
      </Container>
    </Modal>
  );
};
